import React, { useCallback } from 'react';
import { TEMPLATES_LIST_QUERY } from '../queries';
import { DOCUMENT_TEMPLATE_DELETED } from '../subscriptions';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { getListBrowserStore } from '@ts-private/list-browser';
import { Card } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_DOCUMENT_TEMPLATE } from '../mutations';
import DocumentTemplateItem from './DocumentTemplateItem';
import useSubscription from 'hooks/apollo/useSubscription';

export const documentTemplatesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'templateName',
      caption: 'DocumentTemplate.TemplateName',
      type: 'text'
    },
    {
      name: 'templateCode',
      caption: 'DocumentTemplate.TemplateCode',
      type: 'text'
    },
    {
      name: 'bulkPrintingYN',
      caption: 'DocumentTemplate.BulkPrinting',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'DocumentTemplate.TemplateName',
      value: 'TemplateName'
    }
  ],
  pager: {
    orderBy: 'TemplateName',
    direction: 'asc'
  }
});

const DocumentTemplateListBrowser = () => {
  const [t] = useTranslation();
  const confirm = useConfirmDialog();
  const addToast = useToast();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: TEMPLATES_LIST_QUERY,
        name: 'templates'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(DOCUMENT_TEMPLATE_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('DocumentTemplate.DeleteSucceeded'), 'success');
      client.cache.evict({
        id: `DocumentTemplateItem:{"documentTemplateId":${data.documentTemplateDeleted.documentTemplateId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'templates' });
      client.cache.gc();
    }
  });

  const [deleteDocumentTemplate] = useMutation(DELETE_DOCUMENT_TEMPLATE);

  const handleDelete = useCallback(
    documentTemplateId => {
      confirm(
        () => {
          deleteDocumentTemplate({ variables: { input: { documentTemplateId } } });
        },
        {
          content: t('DocumentTemplate.DeleteDocumentTemplate')
        }
      );
    },
    [deleteDocumentTemplate, confirm, t]
  );

  // #endregion

  const itemComponent = useCallback(({ data }) => <DocumentTemplateItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'templateName', caption: 'DocumentTemplate.TemplateName' },
      { name: 'templateCode', caption: 'DocumentTemplate.TemplateCode' },
      { name: 'bulkPrinting', caption: 'DocumentTemplate.BulkPrinting', width: 15, align: 'center' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='document-template-browser' store={documentTemplatesStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={documentTemplatesStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={documentTemplatesStore} options={fetchingOptions} />
    </Card>
  );
};

export default DocumentTemplateListBrowser;
